import { conversationHistory } from '../../lib/janetConversation.js'
// JanetChatPanel — ONE Janet conversation surface, shared by every host:
//   • JanetFAB            (layout='fab'    — floating panel, bottom-right)
//   • JanetChatPage       (layout='window' — /janet/chat with the topics sidebar,
//                          and the bare /janet/window route the desktop shell opens)
//   • DealInspector       (layout='docked' — the deal Workroom, nothing floats)
// The host owns WHICH topic is active (sessionId) and the shell chrome; the panel
// owns the conversation: streaming, cards, voice, persistence per topic.
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bot, X, Send, Mail, CheckCircle2, Loader2, Layers, Calendar, CalendarDays, RefreshCw, Inbox, Volume2, VolumeX, Square, Mic, MicOff, ExternalLink, ListChecks, Phone, Radio, MessageSquare, Maximize2, ChevronDown, Pin, Search, PanelLeftOpen, PanelLeftClose, Sparkles, ArrowRight, ChevronsRight, Users, AlertTriangle } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { logJanetDecision } from '../../hooks/useJanet'
import { regenerateDraft } from '../../hooks/useJanetQueue'
import { createTask } from '../../hooks/useTasks'
import { useToast } from '../../contexts/ToastContext'
import { useJanetVoice } from '../../hooks/useJanetVoice'
import { useJanetSTT } from '../../hooks/useJanetSTT'
import { useJanetRealtime, releaseVoicePreflight } from '../../hooks/useJanetRealtime'
import { useRingCentral } from '../../contexts/RingCentralContext'
import JanetDraftModal from './JanetDraftModal'
import DesignWorkCard from './DesignWorkCard'
import { buildVoiceContext } from '../../lib/voiceContext'
import { getSessionWithTimeout } from '../../lib/authSession'
import { scopedMessages, updateScopedMessages } from '../../lib/janetMessageState'
import { useJanetMemory, pickCardPayload } from '../../hooks/useJanetMemory'
import { useJanetSessions } from '../../hooks/useJanetSessions'
import { openJanetWindow, isJanetWindow } from '../../lib/janetWindow'
import { useJanetObject } from '../../contexts/JanetObjectContext'
import { zoomNavigate } from '../../lib/zoomNav'
import { objectOpenPrimitive } from '../../lib/janetObjectPath'
import { matchPropertyLookupIntent, classifyPropertyMatches, searchPropertiesForLookup } from '../../lib/propertyContextBridge'
import { checkNavPath } from '../../lib/janetNavigation'
import { validateSourceCitation } from '../../lib/janetSourceCitation'
import { parseAnswerBlocks } from '../../lib/janetAnswerFormat'
import { supabase } from '../../lib/supabase'
import { janetFetch } from '../../lib/janetFetch'
import { displayDate } from '../../lib/dateUtil.js'
import { isDurableDraftOffer } from '../../lib/janetOffers'
import { ruleOnOffer, fetchOffer, acceptOfferWithInstruction } from '../../hooks/useJanetOffers'
import { ruleOnNoticing } from '../../hooks/useJanetNoticings'
import { addPersonalTask } from '../../hooks/usePersonalTasks'
import { safeHeadsUpLinks } from '../../lib/headsUpLinks'

// ─── Suggested prompts per page ──────────────────────────────────────────────

function buildSuggestedPrompts(page) {
  if (!page?.entityType) {
    return [
      { icon: Inbox,    text: "What's on my plate today?" },
      { icon: Mail,     text: 'Draft a follow-up…' },
      { icon: Calendar, text: "What's on my calendar this week?" },
    ]
  }
  const type = String(page.entityType).toLowerCase()
  const name = page.entityName || 'this contact'
  if (type === 'contact') {
    return [
      { icon: CheckCircle2, text: `What should I know before calling ${name}?` },
      { icon: Mail,         text: `Draft a follow-up to ${name}` },
      { icon: Calendar,     text: `Find a time to meet with ${name}` },
    ]
  }
  if (type === 'deal') {
    return [
      { icon: CheckCircle2, text: "What's the status of this deal?" },
      { icon: Mail,         text: 'Draft a scheduling email to the counterparty' },
      { icon: Inbox,        text: 'Summarize recent activity on this deal' },
    ]
  }
  if (type === 'project') {
    return [
      { icon: CheckCircle2, text: 'Brief me on this project' },
      { icon: Mail,         text: 'Draft a follow-up on this project' },
      { icon: Inbox,        text: 'What are the open tasks here?' },
    ]
  }
  if (type === 'property') {
    return [
      { icon: CheckCircle2, text: 'Where does this property stand?' },
      { icon: Mail,         text: 'Draft a follow-up on this property' },
    ]
  }
  if (type === 'opportunity') {
    return [
      { icon: CheckCircle2, text: 'Brief me on this opportunity' },
      { icon: Mail,         text: 'Draft an intro / submission for this' },
    ]
  }
  if (type === 'client') {
    return [
      { icon: CheckCircle2, text: 'Brief me on this client' },
      { icon: Mail,         text: 'Draft a check-in to this client' },
    ]
  }
  if (type === 'company') {
    return [
      { icon: CheckCircle2, text: 'Brief me on this company' },
      { icon: Mail,         text: 'Draft an outreach to someone here' },
    ]
  }
  return [
    { icon: Inbox, text: "What's on my plate today?" },
    { icon: Mail,  text: 'Draft a follow-up…' },
  ]
}

// ─── Message components ─────────────────────────────────────────────────────

function UserBubble({ text }) {
  return (
    <div className="flex justify-end">
      <div className="min-w-0 max-w-full md:max-w-[85%] [overflow-wrap:anywhere] rounded-2xl rounded-br-sm bg-[color:var(--ns-ink)] text-white px-3.5 py-2 text-base leading-relaxed md:text-sm md:leading-snug whitespace-pre-wrap">
        {text}
      </div>
    </div>
  )
}

function JanetBubble({ children }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-6 h-6 rounded-full bg-[color:var(--ns-hair-soft)] flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-[10px] font-bold text-[color:var(--ns-ink)]">J</span>
      </div>
      <div className="min-w-0 max-w-full md:max-w-[85%] [overflow-wrap:anywhere] rounded-2xl rounded-tl-sm bg-[color:var(--ns-paper)] border border-[color:var(--ns-hair)] px-3.5 py-2 text-base leading-relaxed md:text-sm md:leading-snug text-[color:var(--ns-ink)]">
        {children}
      </div>
    </div>
  )
}

// ─── Safe readable formatting (issue #254 finding 4) ─────────────────────────
// Renders parseAnswerBlocks' plain-data block/span tree as ordinary React
// elements — every span's text reaches the DOM through a normal React text
// node (React's own escaping), never `dangerouslySetInnerHTML`, so there is
// no path from the model's own text to live markup, an unsafe link, or raw
// protocol/citation scaffolding. The only clickable destinations this
// surface ever renders are the independently-validated chips in
// AnswerSources — formatted answer text itself is never a link.
function AnswerSpans({ spans }) {
  return spans.map((s, i) => {
    if (s.type === 'bold') return <strong key={i} className="font-semibold">{s.text}</strong>
    if (s.type === 'code') {
      return (
        <code key={i} className="px-1 py-0.5 rounded bg-[color:var(--ns-hair-soft)] text-[0.85em] font-mono">
          {s.text}
        </code>
      )
    }
    return <span key={i}>{s.text}</span>
  })
}

export function FormattedAnswer({ text }) {
  const blocks = parseAnswerBlocks(text)
  if (!blocks.length) return null
  return (
    <div className="space-y-2">
      {blocks.map((b, bi) => {
        if (b.type === 'pre') {
          return (
            <pre key={bi} className="whitespace-pre-wrap break-words rounded-lg bg-[color:var(--ns-hair-soft)] px-2.5 py-2 text-[0.85em] font-mono text-[color:var(--ns-ink)] overflow-x-auto">
              <code>{b.text}</code>
            </pre>
          )
        }
        if (b.type === 'ul') {
          return (
            <ul key={bi} className="list-disc pl-5 space-y-0.5">
              {b.items.map((spans, ii) => <li key={ii}><AnswerSpans spans={spans} /></li>)}
            </ul>
          )
        }
        if (b.type === 'ol') {
          return (
            <ol key={bi} className="list-decimal pl-5 space-y-0.5">
              {b.items.map((spans, ii) => <li key={ii}><AnswerSpans spans={spans} /></li>)}
            </ol>
          )
        }
        return (
          <p key={bi}>
            {b.lines.map((spans, li) => (
              <span key={li}>
                {li > 0 && <br />}
                <AnswerSpans spans={spans} />
              </span>
            ))}
          </p>
        )
      })}
    </div>
  )
}

function TypingIndicator() {
  return (
    <JanetBubble>
      <div className="flex gap-1 py-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--ns-ink)] animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--ns-ink)] animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--ns-ink)] animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </JanetBubble>
  )
}

// Renders after the chat endpoint has already inserted pending rows into
// project_milestones server-side. The card is informational + a one-click
// link to /projects/:id?tab=schedule where the principal Accepts / Edits /
// Rejects each row. Failure path: server insert failed → show error.
function ScheduleProposalCard({ payload }) {
  const navigate = useNavigate()
  if (!payload) return null

  if (payload.ok === false) {
    return (
      <div className="mt-2 border border-red-200 rounded-xl bg-[color:var(--ns-raise)] overflow-hidden">
        <div className="px-3 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2">
          <ListChecks size={12} className="text-red-600" />
          <span className="text-[11px] font-semibold text-red-800 uppercase tracking-wide">Schedule proposal — failed</span>
        </div>
        <div className="px-3 py-2 text-[11px] text-red-700">{payload.error || 'Insert failed'}</div>
      </div>
    )
  }

  const items = payload.items || []
  const count = payload.count ?? items.length
  const reviewLink = payload.review_link || (payload.project_id ? `/projects/${payload.project_id}?tab=schedule` : null)

  function fmtDate(d) {
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return d || ''
    return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  return (
    <div className="mt-2 border border-amber-200 rounded-xl bg-[color:var(--ns-raise)] overflow-hidden">
      <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
        <ListChecks size={12} className="text-amber-700" />
        <span className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide">
          {count} item{count !== 1 ? 's' : ''} proposed{payload.project_title ? ` · ${payload.project_title}` : ''}
        </span>
      </div>
      <div className="px-3 py-2 space-y-1.5">
        {payload.source_note && (
          <p className="text-[11px] text-[color:var(--ns-ink-2)] italic">{payload.source_note}</p>
        )}
        {items.slice(0, 6).map((it, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-[11px] text-[color:var(--ns-ink)]">
            <span className="font-medium truncate">{it.title}</span>
            <span className="text-[color:var(--ns-ink-2)] shrink-0">{fmtDate(it.due_date)}</span>
          </div>
        ))}
        {items.length > 6 && (
          <p className="text-[10px] text-[color:var(--ns-faint)]">+ {items.length - 6} more on the Schedule tab</p>
        )}
        {payload.skipped?.length > 0 && (
          <p className="text-[10px] text-amber-600">Skipped: {payload.skipped.length} invalid item{payload.skipped.length !== 1 ? 's' : ''}</p>
        )}
      </div>
      <button
        onClick={() => reviewLink && navigate(reviewLink)}
        disabled={!reviewLink}
        className="w-full px-3 py-2 bg-[color:var(--ns-ink)] text-white text-xs font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
      >
        Review on Schedule tab <ExternalLink size={11} />
      </button>
    </div>
  )
}

function DraftEmailCard({ payload, queuedDraft, onGenerate }) {
  const navigate = useNavigate()
  const typeLabel = {
    scheduling:       'Scheduling',
    follow_up:        'Follow-Up',
    submission_intro: 'Submission intro',
    logistics:        'Logistics / confirm',
    introduction:     'Introduction',
  }[payload.draftType] || 'Email'
  const allRecipients = payload.draftType === 'introduction' && payload.additionalRecipients?.length
    ? [{ name: payload.contactName, email: payload.contactEmail }, ...payload.additionalRecipients]
    : null
  return (
    <div className="mt-2 border border-[color:var(--ns-hair)] rounded-xl bg-[color:var(--ns-raise)] overflow-hidden">
      <div className="px-3 py-2 bg-[color:var(--ns-hair-soft)] border-b border-[color:var(--ns-hair)] flex items-center gap-2">
        <Mail size={12} className="text-[color:var(--ns-ink)]" />
        <span className="text-[11px] font-semibold text-[color:var(--ns-ink)] uppercase tracking-wide">{typeLabel} draft</span>
      </div>
      <div className="px-3 py-2 space-y-1">
        {allRecipients ? (
          <p className="text-xs text-[color:var(--ns-ink)]">
            <span className="text-[color:var(--ns-ink-2)]">To:</span>{' '}
            <span className="font-medium">{allRecipients.map(r => r.name).join(' & ')}</span>
          </p>
        ) : (
        <p className="text-xs text-[color:var(--ns-ink)]">
          <span className="text-[color:var(--ns-ink-2)]">To:</span>{' '}
          <span className="font-medium">{payload.contactName || '—'}</span>
          {payload.contactEmail && <span className="text-[color:var(--ns-faint)]"> · {payload.contactEmail}</span>}
        </p>
        )}
        {payload.context && (
          <p className="text-[11px] text-[color:var(--ns-ink-2)] italic line-clamp-2">{payload.context}</p>
        )}
      </div>
      {queuedDraft?.queueId ? (
        // Draft row already exists in janet_draft_queue (body generating in
        // the background) — review/send happens there, not in a second modal.
        <button
          onClick={() => navigate(queuedDraft.reviewUrl || `/janet/queue?id=${queuedDraft.queueId}`)}
          className="w-full px-3 py-2 bg-[color:var(--ns-ink)] text-white text-xs font-semibold hover:opacity-90 flex items-center justify-center gap-1.5"
        >
          Review & Send in Janet Queue <ExternalLink size={11} />
        </button>
      ) : (
        <button
          onClick={onGenerate}
          disabled={!payload.contactName}
          className="w-full px-3 py-2 bg-[color:var(--ns-ink)] text-white text-xs font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          Generate Draft <Send size={11} />
        </button>
      )}
    </div>
  )
}

function CreateTaskCard({ payload, onCreate, creating, onCreated }) {
  const [calAdded, setCalAdded]   = useState(false)
  // Personal tasks go on the private personal list, never the team list. A
  // personal proposal stays personal unless the person switches it off —
  // EXCEPT when `personalLocked` says this turn actually read personal
  // mail/texts/the personal list: that's a confidentiality boundary, not a
  // classification preference, so the toggle must not be able to un-personal
  // it into the shared `tasks` table (api/_lib/personal-mail.js
  // personalizeTaskPayload).
  const locked = payload.personalLocked === true
  const [personal, setPersonal]   = useState(locked || payload.personal === true)

  const qColor = {
    1: 'bg-red-100 text-red-700',
    2: 'bg-[color:var(--ns-hair-soft)] text-[color:var(--ns-ink)]',
    3: 'bg-amber-100 text-amber-700',
    4: 'bg-[color:var(--ns-hair-soft)] text-[color:var(--ns-ink-2)]',
  }[payload.quadrant] || 'bg-[color:var(--ns-hair-soft)] text-[color:var(--ns-ink-2)]'

  function handleAddToCalendar(userId) {
    if (!payload.dueDate || calAdded) return
    // Optimistic: mark added immediately, run the Calendar write in background.
    setCalAdded(true)
    fetch('/api/create-calendar-events', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        userId,
        events: [{ title: payload.title, start_datetime: payload.dueDate, description: payload.notes || null }],
      }),
    }).catch(() => { /* fail silently — calendar is optional */ })
  }

  return (
    <div className="mt-2 border border-[color:var(--ns-hair)] rounded-xl bg-[color:var(--ns-raise)] overflow-hidden">
      <div className="px-3 py-2 bg-[color:var(--ns-paper)] border-b border-[color:var(--ns-hair)] flex items-center gap-2">
        <CheckCircle2 size={12} className="text-[color:var(--ns-ink-2)]" />
        <span className="text-[11px] font-semibold text-[color:var(--ns-ink)] uppercase tracking-wide">Task — ready to create</span>
      </div>
      <div className="px-3 py-2 space-y-1">
        <p className="text-xs text-[color:var(--ns-ink)] font-medium">{payload.title}</p>
        <div className="flex items-center gap-2 text-[11px] text-[color:var(--ns-ink-2)]">
          {payload.dueDate && <span>Due {payload.dueDate}</span>}
          {payload.quadrant && <span className={`px-1.5 py-0.5 rounded ${qColor} text-[10px] font-semibold`}>Q{payload.quadrant}</span>}
        </div>
        {payload.notes && <p className="text-[11px] text-[color:var(--ns-ink-2)] italic line-clamp-2">{payload.notes}</p>}
        <label className="flex items-center gap-1.5 text-[11px] text-[color:var(--ns-ink-2)] pt-1">
          <input
            type="checkbox"
            checked={personal}
            disabled={locked}
            onChange={e => !locked && setPersonal(e.target.checked)}
            aria-label="Personal task"
          />
          Personal — only you, your assistant and Janet see it
        </label>
        {locked && (
          <p className="text-[10px] text-[color:var(--ns-ink-2)] italic">
            Kept personal — this came from private mail, texts, or your personal list.
          </p>
        )}
      </div>
      <button
        onClick={() => onCreate(locked || personal ? null : handleAddToCalendar, { personal: locked || personal })}
        disabled={creating || !payload.title}
        className="w-full px-3 py-2 bg-[color:var(--ns-ink)] text-white text-xs font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
      >
        {creating ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
        {creating ? 'Creating…' : (locked || personal) ? 'Add to my personal list' : 'Create task'}
      </button>
    </div>
  )
}

export function CalendarEventsCard({ payload, userId, outcome, onAdded }) {
  const [added, setAdded]     = useState(!!outcome)
  const [adding, setAdding]   = useState(false)
  const [links, setLinks]     = useState(outcome?.created || [])
  const [err, setErr]         = useState(null)

  const events = payload?.events || []

  function fmtDate(dt) {
    if (!dt) return ''
    if (/^\d{4}-\d{2}-\d{2}$/.test(dt)) {
      return new Date(dt + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    }
    return displayDate(dt).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles',
    }) + ' PT'
  }

  async function handleAdd() {
    if (adding || added) return
    setErr(null)
    setAdding(true)
    let result
    try {
      const resp = await janetFetch('/api/create-calendar-events', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events, userId }),
      })
      const json = await resp.json()
      if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`)
      const created = (json.created || []).filter(e => e.id)
      result = { created, errors: json.errors || [], status: created.length === events.length && !json.errors?.length ? 'created' : 'partial_or_failed' }
      setLinks(created)
      if (result.status !== 'created') setErr('Some events were not confirmed. Check your calendar before trying again.')
    } catch (e) {
      result = { created: [], status: 'unconfirmed', error: e.message || 'Could not confirm calendar write' }
      setErr(`${result.error}. Check your calendar before trying again.`)
    }
    // Disable replay after partial/unknown results: a lost response can still
    // mean the provider committed a write. Only endpoint IDs count as success.
    setAdded(true)
    setAdding(false)
    onAdded?.(result)
  }

  return (
    <div className="mt-2 border border-teal-200 rounded-xl bg-[color:var(--ns-raise)] overflow-hidden">
      <div className="px-3 py-2 bg-teal-50 border-b border-teal-100 flex items-center gap-2">
        <Calendar size={12} className="text-teal-600" />
        <span className="text-[11px] font-semibold text-teal-800 uppercase tracking-wide">
          {added ? (links.length ? `${links.length} event${links.length !== 1 ? 's' : ''} confirmed` : 'Calendar write not confirmed') : `${events.length} event${events.length !== 1 ? 's' : ''} — ready to add`}
        </span>
      </div>
      <div className="px-3 py-2 space-y-1.5">
        {payload?.source_note && !added && (
          <p className="text-[11px] text-[color:var(--ns-ink-2)] italic">{payload.source_note}</p>
        )}
        {added ? (
          links.map((e, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[11px] text-teal-700">
              <CheckCircle2 size={10} />
              <span className="font-medium truncate">{e.title}</span>
              {e.link && (
                <a href={e.link} target="_blank" rel="noreferrer" className="shrink-0 text-teal-500 hover:text-teal-700">
                  <ExternalLink size={9} />
                </a>
              )}
            </div>
          ))
        ) : (
          events.map((e, i) => (
            <div key={i} className="pb-1.5 border-b border-[color:var(--ns-hair)] last:border-0">
              <p className="text-xs text-[color:var(--ns-ink)] font-medium">{e.title}</p>
              <p className="text-[11px] text-[color:var(--ns-ink-2)]">{fmtDate(e.start_datetime)}</p>
              {e.location && <p className="text-[11px] text-[color:var(--ns-faint)] truncate">{e.location}</p>}
              {e.attendees?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {e.attendees.map((a, j) => (
                    <span key={j} className="text-[10px] bg-teal-50 text-teal-600 border border-teal-200 rounded px-1 py-0.5">{a}</span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
        {err && <p className="text-[11px] text-red-600">{err}</p>}
      </div>
      {!added && (
        <button
          onClick={handleAdd}
          disabled={!events.length || adding}
          className="w-full px-3 py-2 bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          <Calendar size={11} />
          {adding ? 'Adding…' : 'Add to Calendar'}
        </button>
      )}
    </div>
  )
}

// ─── Outbound call proposal card ─────────────────────────────────────────────

function SchedulingApprovalCard({ payload, user }) {
  const [sending, setSending]   = useState(false)
  const [sent, setSent]         = useState(false)
  const [err, setErr]           = useState(null)
  // Staged conversation — the proposal also lives in janet_conversations +
  // an approval email, so closing this tab doesn't lose it. Whichever path
  // answers first (this button, or a reply to Janet's email) wins.
  const [convId, setConvId]     = useState(payload.conversation_id || null)
  const [routedTo, setRoutedTo] = useState(null) // approver ≠ current user (e.g. Kat for clients)
  const stagedRef = useRef(false)

  useEffect(() => {
    if (stagedRef.current || payload.conversation_id) return
    stagedRef.current = true
    ;(async () => {
      try {
        const { data: { session } } = await getSessionWithTimeout()
        const token = session?.access_token || ''
        const resp = await fetch('/api/janet-scheduling-outreach', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            stage: 'request_approval',
            principal_user_id: user?.id,
            principal_name: payload.principal_name,
            principal_email: payload.principal_email,
            meeting_purpose: payload.meeting_purpose,
            meeting_format: payload.meeting_format || 'zoom',
            duration_minutes: payload.duration_minutes || 30,
            proposed_slots: payload.proposed_slots,
            recipients: payload.recipients,
          }),
        })
        const json = await resp.json()
        if (resp.ok && json.conversation_id) {
          setConvId(json.conversation_id)
          const approverEmail = (json.approver?.email || '').toLowerCase()
          if (approverEmail && approverEmail !== (payload.principal_email || '').toLowerCase()) {
            setRoutedTo(json.approver?.name || approverEmail)
          }
        }
      } catch { /* card still works without staging */ }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleApprove() {
    if (sending || sent) return
    setSending(true)
    setErr(null)
    try {
      const { data: { session } } = await getSessionWithTimeout()
      const token = session?.access_token || ''
      const resp = await fetch('/api/janet-scheduling-outreach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          principal_user_id: user?.id,
          principal_name: payload.principal_name,
          principal_email: payload.principal_email,
          meeting_purpose: payload.meeting_purpose,
          meeting_format: payload.meeting_format || 'zoom',
          duration_minutes: payload.duration_minutes || 30,
          proposed_slots: payload.proposed_slots,
          recipients: payload.recipients,
          conversation_id: convId,
        }),
      })
      const json = await resp.json()
      if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`)
      setSent(true)
    } catch (e) {
      setErr(e.message || 'Failed to send')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="mt-2 border border-[color:var(--ns-hair)] rounded-xl bg-[color:var(--ns-raise)] overflow-hidden">
      <div className="px-3 py-2 bg-[color:var(--ns-hair-soft)] border-b border-[color:var(--ns-hair)] flex items-center gap-2">
        <CalendarDays size={12} className="text-[color:var(--ns-ink)]" />
        <span className="text-[11px] font-semibold text-[color:var(--ns-ink)] uppercase tracking-wide">
          {sent ? 'Scheduling outreach sent' : 'Scheduling approval needed'}
        </span>
      </div>
      <div className="px-3 py-2 space-y-2">
        <p className="text-xs font-medium text-[color:var(--ns-ink)]">{payload.meeting_purpose}</p>
        <p className="text-[11px] text-[color:var(--ns-ink-2)]">
          To: {(payload.recipients || []).map(r => r.name).join(', ')} · {payload.duration_minutes || 30} min · {payload.meeting_format || 'Zoom'}
        </p>
        <div className="space-y-1">
          {(payload.proposed_slots || []).map((slot, i) => (
            <div key={i} className="text-[11px] text-[color:var(--ns-ink)] flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-[color:var(--ns-hair-soft)] text-[color:var(--ns-ink)] flex items-center justify-center text-[9px] font-bold shrink-0">{i + 1}</span>
              {slot.label}
            </div>
          ))}
        </div>
        {err && <p className="text-[11px] text-red-600">{err}</p>}
        {sent && (
          <p className="text-[11px] text-green-700 flex items-center gap-1">
            <CheckCircle2 size={11} /> Emails sent — Janet is tracking replies
          </p>
        )}
        {!sent && routedTo && (
          <p className="text-[11px] text-[color:var(--ns-ink-2)]">
            Client scheduling — sent to {routedTo} for approval. You can still approve here.
          </p>
        )}
        {!sent && !routedTo && convId && (
          <p className="text-[11px] text-[color:var(--ns-faint)]">
            Also emailed you — reply there or approve here.
          </p>
        )}
      </div>
      {!sent && (
        <button
          onClick={handleApprove}
          disabled={sending}
          className="w-full px-3 py-2 bg-[color:var(--ns-ink)] text-white text-xs font-semibold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          {sending
            ? <><Loader2 size={11} className="animate-spin" /> Sending…</>
            : <>Approve &amp; Send</>}
        </button>
      )}
    </div>
  )
}

function OutboundCallCard({ payload, onRCDial }) {
  const [activeMode, setActiveMode] = useState(null)  // null | 'direct' | 'monitored' | 'rc'
  const [calling,    setCalling]    = useState(false)
  const [err,        setErr]        = useState(null)

  async function handleTwilioDial(rollMode) {
    if (calling) return
    setCalling(true)
    setErr(null)
    try {
      const { data: { session } } = await getSessionWithTimeout()
      const token = session?.access_token || ''
      const resp = await fetch('/api/twilio-rolling-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          contactName: payload.contactName,
          toNumber:    payload.toNumber,
          contactId:   payload.contactId || null,
          context:     payload.context   || null,
          rollMode,
        }),
      })
      const json = await resp.json()
      if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`)
      setActiveMode(rollMode)
    } catch (e) {
      setErr(e.message || 'Call failed')
    } finally {
      setCalling(false)
    }
  }

  function handleRCDial() {
    onRCDial?.(payload.toNumber)
    setActiveMode('rc')
  }

  const targetFirst = payload.contactName?.split(' ')[0] || 'them'

  return (
    <div className="mt-2 border border-blue-200 rounded-xl bg-[color:var(--ns-raise)] overflow-hidden">
      <div className="px-3 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
        <Phone size={12} className="text-blue-600" />
        <span className="text-[11px] font-semibold text-blue-800 uppercase tracking-wide">
          Outbound call — ready
        </span>
      </div>
      <div className="px-3 py-2 space-y-1">
        {payload.contactName && (
          <p className="text-xs text-[color:var(--ns-ink)] font-medium">{payload.contactName}</p>
        )}
        <p className="text-[11px] text-[color:var(--ns-ink-2)] font-mono">{payload.toNumber}</p>
        {payload.context && (
          <p className="text-[11px] text-[color:var(--ns-ink-2)] italic">"{payload.context}"</p>
        )}
        {err && <p className="text-[11px] text-red-600">{err}</p>}
      </div>

      {activeMode === 'direct' && (
        <div className="px-3 py-2 flex items-center gap-1.5 text-[11px] text-blue-700">
          <CheckCircle2 size={11} /> RC desktop ringing — Janet connects you to {targetFirst} when you answer
        </div>
      )}
      {activeMode === 'monitored' && (
        <div className="px-3 py-2 flex items-center gap-1.5 text-[11px] text-[color:var(--ns-ink)]">
          <CheckCircle2 size={11} /> RC desktop ringing — Janet screens {targetFirst} and stays on the call
        </div>
      )}
      {activeMode === 'rc' && (
        <div className="px-3 py-2 flex items-center gap-1.5 text-[11px] text-blue-700">
          <CheckCircle2 size={11} /> RC widget opening — pick up when ready
        </div>
      )}

      {!activeMode && (
        <div className="flex flex-col border-t border-blue-100">
          {/* Row 1: Twilio-routed calls (Janet in the path) */}
          <div className="flex">
            <button
              onClick={() => handleTwilioDial('direct')}
              disabled={calling || !payload.toNumber}
              className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              title="Janet connects you directly — no screening, drops off once bridged"
            >
              {calling ? <Loader2 size={11} className="animate-spin" /> : <Phone size={11} />}
              Direct Connect
            </button>
            <button
              onClick={() => handleTwilioDial('monitored')}
              disabled={calling || !payload.toNumber}
              className="flex-1 px-3 py-2 bg-[color:var(--ns-ink)] text-white text-xs font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 border-l border-[color:var(--ns-ink)]"
              title="Janet screens, bridges, then stays on the call listening and responding to commands"
            >
              {calling ? <Loader2 size={11} className="animate-spin" /> : <Phone size={11} />}
              Monitored Call
            </button>
          </div>
          {/* Row 2: Pure RC dial (no Janet) */}
          <button
            onClick={handleRCDial}
            disabled={!payload.toNumber}
            className="w-full px-3 py-1.5 bg-[color:var(--ns-paper)] text-[color:var(--ns-ink-2)] text-[11px] hover:bg-[color:var(--ns-hair-soft)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 border-t border-blue-100"
            title="Dial directly via RC — no Janet involvement"
          >
            <Phone size={10} /> RC direct dial only
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Open-context card (issue #169 Slice 6A) ─────────────────────────────────
// Generic click-through door into a canonical object's visual room — today
// only 'property' actually resolves (the deterministic entry bridge below),
// but the card and the payload shape are type-agnostic so a future tool-call
// path (Deal/Person/Expression) can reuse this without a new card type.
//
// payload.root distinguishes WHY the card exists (2026-09-29 repair): the
// deterministic property-lookup bridge below is a fresh, top-level
// natural-language entry — "let's look at where we are with X" typed cold —
// with no relation to whatever object trail happens to already be on Janet's
// stack, so it must establish X as a NEW root (visitObject), never extend
// whatever was there before. A genuine contextual lateral move (a door
// clicked from inside an already-open room) still extends the trail
// (pushObject) — see janetObjectPath.js's objectOpenPrimitive for the full
// reasoning. Both are the same two pre-existing context primitives; no new
// navigation mechanism.
// Opening a page Janet named (Aaron, 2026-10-08: "take me anywhere within
// Chatrone"). In her own window (/janet/window) there is no app around her, so
// the page opens in the window that opened her, else a new one.
// Re-checked here, through the SAME canonical route contract the server
// applied before this payload was ever sent (api/_lib/janet-nav.js
// resolveNavProposal → src/lib/janetNavigation.js checkNavPath), rather than
// trusting payload.path/payload.external on the strength of them already
// being in state. This function is the ONE place a navigation/citation
// payload is actually acted on, and it is reached two ways that must behave
// identically: a payload that just arrived fresh off the stream this turn,
// and a card restored from a reloaded topic's persisted `payload` JSONB
// (useJanetMemory) — this function has no way to tell which, so both must
// clear the identical bar. Only the re-validated, re-derived path below is
// ever used to navigate or open a window — never the raw stored string
// (issue #254: "no invented IDs or arbitrary unsafe URLs").
export function openJanetDestination(payload, { navigate, pushObject, visitObject }) {
  if (!payload?.path) return
  const check = checkNavPath(payload.path)
  if (!check.ok) return
  const path = check.path
  // A file that lives only in Google Drive opens there, in a new tab — the
  // checker's own verdict decides this, never the stored payload's own
  // (possibly stale) copy of the flag.
  if (check.external) { window.open(path, '_blank', 'noopener'); return }
  if (isJanetWindow()) {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.location.assign(path)
        window.opener.focus()
        return
      }
    } catch { /* fall through */ }
    window.open(path, '_blank')
    return
  }
  const ref = { type: payload.type || check.kind || 'property', id: payload.id || path, label: payload.title, path }
  if (objectOpenPrimitive(payload) === 'visit') visitObject(ref)
  else pushObject(ref)
  zoomNavigate(navigate, path)
}

const KIND_WORDS = { property: 'Property', expression: 'Version', deal: 'Deal', contact: 'Person', company: 'Company', design_brief: 'Design', page: 'Page', draft: 'Draft', stage: 'Stage', drive_file: 'Google Drive file', client: 'Client', task: 'Task', opportunity: 'Opportunity', submission: 'Submission', person: 'Person', invoice: 'Invoice' }

export function OpenContextCard({ payload }) {
  const navigate = useNavigate()
  const { pushObject, visitObject } = useJanetObject()
  // A card restored from a reloaded topic (or any future source) that no
  // longer clears checkNavPath's bar renders NOTHING rather than a door
  // that looks live and silently does nothing on click — a missing/invalid
  // reference is reported by its absence, never shown as a working
  // citation (issue #254).
  if (!payload?.id || !payload?.path || !checkNavPath(payload.path).ok) return null
  const open = () => openJanetDestination(payload, { navigate, pushObject, visitObject })
  return (
    <div className="mt-2 border border-janet-200 rounded-xl bg-[color:var(--ns-raise)] overflow-hidden">
      <div className="px-3 py-2 flex items-center gap-2 bg-janet-50">
        <Sparkles size={12} className="text-janet-600" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-janet-700">
          {KIND_WORDS[payload.type] || 'Record'}
        </span>
      </div>
      <div className="px-3 py-2">
        <p className="text-sm font-semibold text-[color:var(--ns-ink)]">{payload.title}</p>
        {payload.subtitle && <p className="text-[11px] text-[color:var(--ns-ink-2)] mt-0.5">{payload.subtitle}</p>}
      </div>
      <button
        onClick={open}
        className="w-full px-3 py-2 text-xs font-semibold text-white bg-janet-700 hover:bg-janet-600 flex items-center justify-center gap-1.5"
      >
        Open {payload.title} <ArrowRight size={12} />
      </button>
    </div>
  )
}

// ─── Answer sources (issue #254 finding 3) ───────────────────────────────────
// Contact + dated-message citations the server already re-validated against
// the real record (api/_lib/janet-chat-citations.js buildSourcesPayload) —
// rendered as small clickable chips under the answer they support, opening
// the SAME existing navigation destinations as every other Janet door
// (OpenContextCard above). Re-validated AGAIN here, through the IDENTICAL
// shared contract the server applied (validateSourceCitation,
// src/lib/janetSourceCitation.js — not just a structural checkNavPath.ok
// check), for the same reason OpenContextCard does: this is reached both by
// a payload fresh off this turn's stream and by a card restored from a
// reloaded topic's persisted `payload` JSONB, and both must clear the
// identical bar — a stale/corrupted/tampered citation renders nothing,
// never a door that looks live and silently does nothing (or navigates
// somewhere it has no business going) on click. The path ever used to
// navigate is the VALIDATOR'S OWN re-derived path, never the raw stored
// `path` field — a persisted source naming a real id but a mismatched path
// (consolidated review round 2 finding 3's `type:'email', id:<realUUID>,
// path:'/settings'` reproduction) opens its own real destination, never
// the stale/tampered one.
const SOURCE_ICONS = { contact: Users, email: Mail }

function sourceDateLabel(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function AnswerSources({ sources, unavailable }) {
  const navigate = useNavigate()
  const { pushObject, visitObject } = useJanetObject()
  const valid = (Array.isArray(sources) ? sources : [])
    .map(validateSourceCitation)
    .filter(Boolean)
  if (!valid.length && !unavailable) return null
  return (
    <div className="mt-2">
      {valid.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {valid.map((s, i) => {
            const Icon = SOURCE_ICONS[s.type] || Mail
            const dateLabel = sourceDateLabel(s.date)
            return (
              <button
                key={`${s.type}-${s.id}-${i}`}
                onClick={() => openJanetDestination({ path: s.path, type: s.type, id: s.id, title: s.label }, { navigate, pushObject, visitObject })}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[color:var(--ns-hair)] bg-[color:var(--ns-raise)] text-[color:var(--ns-ink-2)] hover:border-janet-300 hover:text-janet-700 transition-colors"
              >
                <Icon size={10} />
                <span className="truncate max-w-[180px]">{s.label}</span>
                {dateLabel && <span className="text-[color:var(--ns-faint)]">· {dateLabel}</span>}
              </button>
            )
          })}
        </div>
      )}
      {unavailable && (
        <p className="mt-1 text-[10px] text-amber-700 flex items-center gap-1">
          <AlertTriangle size={10} /> Some sources couldn't be verified just now.
        </p>
      )}
    </div>
  )
}

// A conversation Janet started (api/_lib/janet-openers.js): her opening
// message is the text above; this is the answer row under it. "Yes" marks the
// offer/noticing taken and sends its full instruction into THIS topic, so she
// starts the work right here; "Not now" lets it go. The buttons show only
// while her opener is the last word — once anyone answers, the topic is a
// normal conversation.
function OpenerCard({ payload, live, busy = false, onAccept, onDismiss }) {
  if (!payload?.id) return null
  const sources = (payload.sources || []).filter(s => s?.label).slice(0, 4)
  return (
    <div className="mt-2">
      {sources.length > 0 && (
        <p className="text-[11px] flex flex-wrap gap-x-3 gap-y-0.5 text-[color:var(--ns-ink-2)]">
          {sources.map((s, i) => s.url
            ? <a key={i} href={s.url} target={/^https?:/.test(s.url) ? '_blank' : undefined} rel="noreferrer" className="underline underline-offset-2 inline-flex items-center gap-0.5">{s.label}{/^https?:/.test(s.url) && <ExternalLink size={9} />}</a>
            : <span key={i}>{s.label}</span>)}
        </p>
      )}
      {live && (
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {(payload.actions?.length ? payload.actions : [{ label: payload.acceptLabel || 'Yes, do it', text: payload.acceptText }]).map((a, i) => (
            <button key={i} onClick={() => onAccept(a.text)} disabled={busy} className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-60 ${i === 0 ? 'text-white bg-janet-700 hover:bg-janet-600' : 'text-janet-800 bg-janet-50 hover:bg-janet-100 border border-janet-200'}`}>
              {busy && i === 0 ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {a.label}
            </button>
          ))}
          {payload.kind === 'noticing' && <button onClick={() => onDismiss('acknowledged')} disabled={busy} className="text-xs font-semibold underline underline-offset-2 text-[color:var(--ns-ink-2)] disabled:opacity-60">I've seen this</button>}
          <button onClick={() => onDismiss()} disabled={busy} className="text-xs font-semibold underline underline-offset-2 text-[color:var(--ns-ink-2)] disabled:opacity-60">
            {payload.dismissLabel || 'Not now'}
          </button>
        </div>
      )}
    </div>
  )
}

// Offers and noticings have a ruling to record; a meeting reminder doesn't.
const ruleFor = (op) => (op.kind === 'noticing' ? ruleOnNoticing : op.kind === 'offer' ? ruleOnOffer : null)

// ─── Main FAB ────────────────────────────────────────────────────────────────

const cacheKey = (sessionId, userId) => `janet.chat.messages.${userId}.${sessionId}`
function readCache(sessionId, userId) {
  if (!sessionId || !userId) return []
  try {
    const raw = localStorage.getItem(cacheKey(sessionId, userId))
    if (!raw) return []
    const { msgs } = JSON.parse(raw)
    return Array.isArray(msgs) ? msgs : []
  } catch { return [] }
}

// Header dropdown: the recent topics, pinned first. The full list with rename /
// pin / archive lives on /janet/chat — this is the quick "resume" door.
function relTime(iso) {
  if (!iso) return ''
  const d = Date.now() - new Date(iso).getTime()
  const m = Math.round(d / 60000)
  if (m < 1) return 'now'
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h`
  const days = Math.round(h / 24)
  if (days < 7) return `${days}d`
  return displayDate(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function TopicsDropdown({ sessions, activeId, onPick, onNew, onOpenAll, onClose }) {
  const ref = useRef(null)
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [onClose])
  const recent = sessions.slice(0, 12)
  return (
    <div ref={ref} className="absolute left-2 right-2 top-[calc(100%-6px)] z-30 rounded-xl border border-[color:var(--ns-hair)] bg-[color:var(--ns-raise)] shadow-xl text-[color:var(--ns-ink)] overflow-hidden">
      <button onClick={onNew} className="w-full text-left px-3 py-2 text-xs font-semibold text-janet-700 hover:bg-janet-50 flex items-center gap-2 border-b border-[color:var(--ns-hair)]">
        <RefreshCw size={11} /> New topic
      </button>
      <div className="max-h-72 overflow-y-auto py-1">
        {recent.length === 0 && <p className="px-3 py-3 text-[11px] text-[color:var(--ns-faint)]">No earlier topics yet.</p>}
        {recent.map(s => (
          <button
            key={s.id}
            onClick={() => onPick(s.id)}
            className={`w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-[color:var(--ns-paper)] ${s.id === activeId ? 'bg-janet-50' : ''}`}
          >
            {s.pinned && <Pin size={10} className="text-janet-500 shrink-0" />}
            <span className="flex-1 min-w-0 truncate text-xs">{s.title || 'Conversation'}</span>
            {s.entity_name && <span className="text-[10px] text-[color:var(--ns-faint)] truncate max-w-[80px]">{s.entity_name}</span>}
            <span className="text-[10px] text-[color:var(--ns-faint)] shrink-0">{relTime(s.last_message_at)}</span>
          </button>
        ))}
      </div>
      <button onClick={onOpenAll} className="w-full text-left px-3 py-2 text-[11px] text-[color:var(--ns-ink-2)] hover:bg-[color:var(--ns-paper)] border-t border-[color:var(--ns-hair)] flex items-center gap-2">
        <Search size={11} /> All topics · full view
      </button>
    </div>
  )
}

const AUTO_ASKED = new Set()

export default function JanetChatPanel({
  layout = 'fab',            // 'fab' | 'window' | 'docked'
  sessionId,                 // active topic id (host-owned)
  page,                      // { entityType, entityId, entityName, entityContext, autoGreet } or null
  onClose,                   // fab/docked: host closes the panel
  onNewTopic,                // (pageCtx) → host mints a new topic id and makes it active
  onPickTopic,               // (id) → host switches topic
  startListeningOnMount = false,
  startLiveVoiceOnMount = false, // host-initiated: mount already implies a user tap (e.g. HomeJanetDock's "Talk to Janet" doorway) — never set this from an effect that isn't itself gated on a gesture
  voicePreflight = null,     // optional createVoicePreflight() result from the SAME tap that set startLiveVoiceOnMount — mic/audio already acquired synchronously in that gesture (2026-09-30 repair, issue #178); ignored unless startLiveVoiceOnMount is also set
  showTopics = true,         // fab/docked: topics dropdown in the header (window layout has the sidebar)
  onOpenWindow,              // optional override for the "open in window" door
  sidebarOpen,               // window layout: whether the host's topics sidebar is showing
  onToggleSidebar,           // window layout: labeled "Topics" button in the header toggles it
  autoAsk = null,            // { key, sessionId, text } — send `text` once, into that topic (an accepted offer, JanetChatPage)
}) {
  const { user, profile } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const { pushObject, visitObject } = useJanetObject()
  const { dbMessages, saveMessage: saveToDb, loading: dbLoading } = useJanetMemory(user?.id, sessionId)
  const { sessions, available: sessionsAvailable } = useJanetSessions(showTopics ? user?.id : null)
  const activeSession = sessions.find(s => s.id === sessionId) || null

  // Messages for the ACTIVE topic. Paint from the per-topic localStorage cache
  // instantly, then let the DB (truth) replace it when it has at least as much.
  const messageScope = `${user?.id}:${sessionId}`
  const [messageState, setMessageState] = useState(() => ({ scope: messageScope, rows: readCache(sessionId, user?.id) }))
  const messages = scopedMessages(messageState, messageScope)
  const setMessages = useCallback(update => setMessageState(state => updateScopedMessages(state, messageScope, update)), [messageScope])
  useEffect(() => {
    setMessageState(state => state.scope === messageScope ? state : { scope: messageScope, rows: readCache(sessionId, user?.id) })
    setInput('')
  }, [messageScope, sessionId, user?.id])
  useEffect(() => {
    if (dbLoading) return
    setMessages(prev => (dbMessages.length > prev.length ? dbMessages : prev))
  }, [dbMessages, dbLoading, setMessages])

  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [draftPayload, setDraftPayload] = useState(null)
  const [taskCreating, setTaskCreating] = useState({})

  const rc = useRingCentral()
  const { voiceEnabled, toggleVoice, speak, speakChunk, speakFull, flushSpeak, stop: stopVoice, isSpeaking } = useJanetVoice()
  const { isListening, transcript, startListening, stopListening, startWakeWord, stopWakeWord, wakeWordActive, supported: sttSupported } = useJanetSTT()

  const [liveVoiceActive, setLiveVoiceActive] = useState(false)
  const rt = useJanetRealtime()

  // True for as long as this component instance's effect is currently
  // mounted. Checked in handleStartLiveVoice AFTER its own await (the
  // /api/janet-voice-prompt fetch) and before it ever calls rt.start() — a
  // close/unmount mid-flight (e.g. the compact dock's Close, tapped while
  // that fetch is still in flight) must invalidate the in-flight
  // voice-start path rather than resuming afterward and starting a realtime
  // session the user already dismissed (2026-09-30 repair, issue #178).
  //
  // Set inside the effect body, not just as the useRef initializer: React 18
  // StrictMode (src/main.jsx) replays every effect in development —
  // mount → cleanup → mount again — on the SAME component instance, without
  // re-running useRef's initial-value argument. A ref that only ever gets
  // set to false in cleanup would stay false forever after that replay, so
  // every live-voice start would look like a stale unmount and silently
  // never call rt.start() in dev (and in any rendered review of the dev
  // build). Setting it true in the effect body itself makes the guard
  // reflect the CURRENT active effect lifetime, which is correct across a
  // real unmount too (cleanup still runs last and leaves it false).
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const buildRTSystemPrompt = useCallback(() => {
    const name = profile?.full_name || user?.email?.split('@')[0] || 'Aaron'
    const now  = new Date().toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
    const pageCtx = page?.entityName
      ? `The principal is currently viewing: ${page.entityType} — ${page.entityName}.`
      : ''
    return `You are Janet, the executive assistant for Chatrone — a premier entertainment management and production company in Los Angeles. You are speaking with ${name} through the Chatrone OS web application.

CURRENT DATE & TIME: ${now}
${pageCtx}

IDENTITY
You are Janet — professional, warm, and razor-sharp. You know the business inside and out.
You are not a chatbot. You do not ask riddles, make small talk, or offer unsolicited opinions.
If asked whether you are AI: "I'm Janet, Chatrone's virtual assistant." No further elaboration.
Never say "as an AI", "as a language model", or break character in any way.

VOICE STYLE
Short, direct sentences. Executive assistant cadence — like a trusted colleague, not a service agent.
No bullet points, no markdown, no lists read aloud. This is a live voice conversation.
Lead with the answer. Offer more only if asked.
Fill pauses naturally: "Let me pull that up," "One moment," "Sure."

YOUR ROLE
You handle: contacts, deals, projects, properties, tasks, calendar, inbox, industry intelligence.
When asked about a person, project, or deal — search for it. Don't guess.
When asked about calendar — use free_busy. When asked about tasks — use list_tasks.
Proactively connect dots: if someone mentions a name, look them up.

BOUNDARIES
You draft and suggest — the principal approves before anything is sent or created.
You do not make commitments on behalf of Chatrone.
You do not discuss internal financials, deal terms, or client contracts unprompted.`
  }, [profile, user, page])

  // `preflight`, when supplied, is a createVoicePreflight() result — mic +
  // AudioContext already acquired synchronously inside the CALLER's own tap
  // (2026-09-30 repair, issue #178), handed straight through to rt.start
  // instead of letting it acquire its own after this function's awaits have
  // burned through the tap's transient user activation. The header "Live
  // voice" button (and every other caller) passes none, unchanged.
  const handleStartLiveVoice = useCallback(async (preflight = null) => {
    if (rt.status !== 'idle' && rt.status !== 'error') return
    const pageCtx = page ? { entityType: page.entityType, entityId: page.entityId, entityName: page.entityName } : null
    setLiveVoiceActive(true)

    // Load the FULL voice-tuned Janet (interview profile + behavioral model +
    // confidentiality + page context + shared demeanor) from the server so the
    // web voice matches the phone. Fall back to the inline prompt if it fails —
    // voice must never break on a fetch hiccup.
    let prompt
    try {
      const r = await janetFetch('/api/janet-voice-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          surface: 'ui',
          userId: user?.id || null,
          principalName: profile?.full_name || user?.email?.split('@')[0] || null,
          principalEmail: user?.email || null,
          page: pageCtx,
        }),
      })
      if (r.ok) {
        const data = await r.json()
        if (data?.prompt) prompt = data.prompt
      }
    } catch { /* fall through to inline fallback */ }
    if (!prompt) prompt = buildRTSystemPrompt()

    // The host may have closed the dock (or this panel unmounted some other
    // way) while the voice-prompt fetch above was in flight — its own close
    // path already released `preflight` if nothing had claimed it yet. Never
    // resume into rt.start() after that: honor the close (no realtime
    // session may start after one), and release the preflight ourselves too
    // in case nothing else did — releaseVoicePreflight is idempotent, so
    // this is safe even when the host already handled it.
    if (!mountedRef.current) {
      releaseVoicePreflight(preflight)
      return
    }

    rt.start(prompt, pageCtx, preflight)
  }, [rt, buildRTSystemPrompt, page, user, profile])

  const handleStopLiveVoice = useCallback(() => {
    rt.stop()
    setLiveVoiceActive(false)
  }, [rt])

  // Calendar cache — re-fetched at most once per 5 minutes
  const calendarCacheRef = useRef(null)       // { events: [], ts: number }
  const pendingSchedulingRef = useRef(null)   // pending scheduling proposal waiting for "yes"

  const panelRef = useRef(null)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)

  const open = true                       // the panel is open whenever it is mounted
  const setOpen = (v) => { if (!v) onClose?.() }

  const principalName = profile?.full_name || user?.email?.split('@')[0] || 'Aaron'
  const principal = { name: principalName, email: user?.email || '' }

  const prompts = buildSuggestedPrompts(page)


  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, sending])

  // Per-topic localStorage cache (instant paint on resume; the DB is truth)
  useEffect(() => {
    if (!sessionId || !user?.id || messageState.scope !== messageScope) return
    try {
      if (messages.length === 0) localStorage.removeItem(cacheKey(sessionId, user?.id))
      else localStorage.setItem(cacheKey(sessionId, user?.id), JSON.stringify({ ts: Date.now(), msgs: messages.slice(-30).map(m => ({ ...m, streaming: false })) }))
    } catch { /* storage full or unavailable — non-fatal */ }
  }, [messages, sessionId, user?.id, messageState.scope, messageScope])

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100)
  }, [open])

  // ── Auto-glance when any entity detail page is opened ─────────────────────
  // Replaces the previous broad-options auto-greet. Now we call the dedicated
  // /api/janet-entity-glance endpoint, which:
  //   - Reads last 7d of fresh+actionable signals (emails, tasks, drafts, flags)
  //   - Filters out anything already surfaced to this user on this entity
  //     (janet_entity_greetings.source_hash UNIQUE constraint)
  //   - Asks Haiku for the ONE most salient one-line observation + question
  //   - Returns { greeting: null } if nothing new+actionable — FAB stays silent
  // Per Aaron's directive 2026-05-20.
  const greetedEntityRef = useRef(null)
  useEffect(() => {
    if (!open) return
    if (!page?.entityType || !page?.entityId || !page?.autoGreet) return
    if (greetedEntityRef.current === page.entityId) return  // already greeted this entity
    if (!user?.id) return  // glance is keyed on the user — no anon path

    greetedEntityRef.current = page.entityId
    onNewTopic?.(page)   // fresh topic anchored to this entity
    setMessages([])

    janetFetch('/api/janet-entity-glance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entity_type: page.entityType,
        entity_id:   page.entityId,
        user_id:     user.id,
      }),
    })
      .then(r => r.json().catch(() => ({})))
      .then(data => {
        const greeting = (data?.greeting || '').trim()
        if (!greeting) {
          // Silent open — no opening message. The input is focused & ready.
          setMessages([])
          return
        }
        setMessages([{ role: 'assistant', content: greeting }])
        if (voiceEnabled) {
          try { speakChunk(greeting); flushSpeak().catch(() => {}) } catch {}
        }
      })
      .catch(() => {
        // Network failure on glance → just open silently. Don't surface noise.
        setMessages([])
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page?.entityType, page?.entityId, page?.autoGreet, user?.id])

  // Mirror live STT transcript into the input field
  useEffect(() => {
    if (isListening) setInput(transcript)
  }, [transcript, isListening])

  // Deterministic Property-lookup entry bridge (issue #169 Slice 6A) — see
  // propertyContextBridge.js's header for why this is a client-side search
  // rather than a new Janet tool call. Returns true when it fully handled
  // the turn (a synthetic Janet reply was appended), false to fall through
  // to the normal model call unchanged.
  const tryPropertyContextBridge = async (query) => {
    try {
      // searchPropertiesForLookup treats `query` strictly as search DATA via
      // `.ilike()` — never spliced into a raw `.or()` filter string, which
      // reserved punctuation in a valid property name (commas, parentheses,
      // quotes) would corrupt. See propertyContextBridge.js's header.
      const { data, error } = await searchPropertiesForLookup(supabase, query)
      if (error) return false
      const result = classifyPropertyMatches(data)
      // No match — the phrase may not have actually named a property (e.g.
      // "where are we with the Q3 budget"). Let Janet's normal reply try.
      if (result.status === 'none') return false
      if (result.status === 'ambiguous') {
        const names = result.candidates.map(c => c.title).join(', ')
        const content = `A few properties match "${query}": ${names}. Which one did you mean?`
        setMessages(m => [...m, { role: 'assistant', content }])
        saveToDb('assistant', content, { pageContext: page })
        return true
      }
      const { property } = result
      const content = `Here's ${property.title}.`
      const contextPayload = {
        type: 'property',
        id: property.id,
        title: property.title,
        path: `/properties/${property.id}`,
        subtitle: [property.work_form?.replace(/_/g, ' '), property.status?.replace(/_/g, ' ')].filter(Boolean).join(' · '),
        // A fresh, top-level cold entry (2026-09-29 repair) — this bridge
        // never fires for a contextual follow-on inside an already-open
        // room (see propertyContextBridge.js's header), so opening it must
        // establish a NEW context root, not extend whatever trail happened
        // to already be on Janet's object-path stack. See OpenContextCard.
        root: true,
      }
      setMessages(m => [...m, { role: 'assistant', content, actionType: 'open_context', contextPayload }])
      saveToDb('assistant', content, { actionType: 'open_context', pageContext: page, payload: { contextPayload } })
      return true
    } catch {
      return false
    }
  }

  // ── Send message to Janet ─────────────────────────────────────────────────
  const acknowledgmentPending = useRef(false)
  const sendMessage = useCallback(async (text, { acknowledgmentRecorded = false } = {}) => {
    const trimmed = (text || '').trim()
    if (!trimmed || sending) return
    // A typed/voice reply is an explicit response; merely viewing the topic
    // never is. Record it before work starts so delayed escalation stops.
    const opener = messages.at(-1)?.openerPayload
    if (!acknowledgmentRecorded && opener?.kind === 'noticing') {
      if (acknowledgmentPending.current) return
      acknowledgmentPending.current = true
      try {
        const response = await ruleOnNoticing(opener.id, 'acknowledged', 'chat')
        if (!response.ok) { toast.error(response.unavailable ? response.error : "Couldn't record your response — try again."); return }
      } finally { acknowledgmentPending.current = false }
    }

    // Stop STT and any in-flight TTS from the previous turn
    stopListening()
    stopVoice()

    // A typed follow-up can quote the private answer it is responding to.
    // Preserve the same sensitivity on the user's message, including reloads;
    // spoken turns only receive redacted history and do not inherit this flag.
    const userMsg = { role: 'user', content: trimmed,
      personalMail: !voiceEnabled && conversationHistory(messages).some(m => !!m.personalMail) }
    const prior = messages
    userMsg.engineeringRequestId = crypto.randomUUID()
    setMessages(m => [...m, userMsg])
    const savedUserTurn = saveToDb('user', trimmed, { pageContext: page, payload: pickCardPayload(userMsg) })
    setInput('')

    // Deterministic Property-lookup entry bridge (issue #169 Slice 6A) — no
    // token spent, no round trip: resolved straight against the record
    // before anything reaches Janet's model. See tryPropertyContextBridge
    // and propertyContextBridge.js for the documented boundary of what this
    // narrow bridge does and doesn't cover.
    //
    // The lookup itself is still an async Supabase round-trip — it MUST hold
    // the same `sending` in-flight guard the normal model call uses below,
    // or a second Enter/Send fired while it's pending sails straight past
    // the `if (!trimmed || sending) return` guard at the top of this
    // function (sending was still false) and submits a duplicate message
    // (2026-09-29 repair, issue #170). tryPropertyContextBridge already
    // swallows every internal error and always resolves to a plain boolean
    // (never throws), so there is no separate "error path" to unwind here —
    // "handled" (return, sending restored to idle) and "fallback" (sending
    // stays true and the normal flow below takes over, ending in its own
    // try/finally) are the only two outcomes.
    const propertyQuery = matchPropertyLookupIntent(trimmed)
    if (propertyQuery) {
      setSending(true)
      const handled = await tryPropertyContextBridge(propertyQuery)
      if (handled) {
        setSending(false)
        return
      }
      // Not handled — fall through to the normal model call below, which
      // re-arms `sending` (already true) and restores idle in its own
      // finally once that request settles.
    }

    setSending(true)

    let assistantIdx = -1
    let finalPayload = null
    let accumulated = ''
    const abortController = new AbortController()
    // If the stream hangs without ever closing (Anthropic API slow / Vercel
    // function stalls after sending the heartbeat), abort after 90s so
    // setSending(false) always fires and the input unlocks.
    const abortTimer = setTimeout(() => abortController.abort(), 90_000)

    try {
      // Use cached calendar if fresh (< 5 min), otherwise server fetches and we cache the result
      const now = Date.now()
      const cachedCalendar = calendarCacheRef.current && (now - calendarCacheRef.current.ts < 300_000)
        ? calendarCacheRef.current.events
        : null

      // For voice mode, build cache context client-side (~5ms) so the server
      // can answer immediately without a Haiku pre-flight round-trip.
      const voiceCacheContext = voiceEnabled ? buildVoiceContext(trimmed) : null

      await savedUserTurn
      const resp = await janetFetch('/api/janet-chat?stream=1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal,
        body: JSON.stringify({
          message: trimmed,
          sessionId,
          engineeringRequestId: userMsg.engineeringRequestId,
          pageContext: page,
          // `personalMail` round-trips a server-stamped flag (janet-chat.js)
          // marking a turn whose answer actually drew on personal (iCloud)
          // mail — the server redacts it out of history before a later
          // voice-mode turn ever sees it again (sanitizeHistoryForVoice).
          conversationHistory: conversationHistory(prior, { voiceMode: voiceEnabled }),
          userId: user?.id || null,
          principalName,
          voiceMode: voiceEnabled,
          cachedCalendar,
          voiceCacheContext,
          pendingSchedulingContext: pendingSchedulingRef.current || null,
        }),
      })

      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => 'Janet chat failed')
        throw new Error(errText || 'Janet chat failed')
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })

        let blankIdx
        while ((blankIdx = buf.indexOf('\n\n')) !== -1) {
          const block = buf.slice(0, blankIdx)
          buf = buf.slice(blankIdx + 2)

          let event = ''
          let dataLine = ''
          for (const line of block.split('\n')) {
            if (line.startsWith('event: ')) event = line.slice(7).trim()
            else if (line.startsWith('data: ')) dataLine += line.slice(6)
          }
          if (!dataLine) continue

          let payload
          try { payload = JSON.parse(dataLine) } catch { continue }

          if (event === 'text' && payload?.delta) {
            accumulated += payload.delta
            // Voice mode: suppress streaming bubble — typing indicator stays up
            // until done, then text and audio surface together below.
            if (!voiceEnabled) {
              setMessages(m => {
                if (assistantIdx === -1) {
                  assistantIdx = m.length
                  return [...m, { role: 'assistant', content: accumulated, streaming: true, actionType: 'answer' }]
                }
                const next = [...m]
                if (next[assistantIdx]) {
                  next[assistantIdx] = { ...next[assistantIdx], content: accumulated }
                }
                return next
              })
            }
          } else if (event === 'done') {
            finalPayload = payload
            // Cache calendar events returned from server for 5 minutes
            if (Array.isArray(payload?.calendarEvents)) {
              calendarCacheRef.current = { events: payload.calendarEvents, ts: Date.now() }
            }
            // Store or clear pending scheduling context
            if (payload?.schedulingProposal) {
              pendingSchedulingRef.current = payload.schedulingProposal
            } else if (payload?.schedulingProposal === null) {
              pendingSchedulingRef.current = null // explicit clear on send confirmation
            }
          } else if (event === 'error') {
            throw new Error(payload?.error || 'Janet chat error')
          }
        }
      }

      const finalContent = finalPayload?.content || "The response ended before it was complete. No new action is confirmed; please try again."
      const finalType    = finalPayload?.type    || 'answer'
      // A page Janet opens is drawn as the same door the property bridge uses.
      const navTarget    = finalType === 'open_page' ? (finalPayload?.navPayload || null) : null

      let revealed = false
      const revealMessage = () => {
        if (revealed) return
        revealed = true
        setMessages(m => {
          const finalized = {
            engineeringWork: finalPayload?.engineeringWork || null,
            role: 'assistant',
            content: finalContent,
            streaming: false,
            actionType: navTarget ? 'open_context' : finalType,
            contextPayload:  navTarget,
            queuedDraft:     finalPayload?.queuedDraft     || null,
            draftPayload:    finalPayload?.draftPayload    || null,
            taskPayload:     finalPayload?.taskPayload     || null,
            calendarPayload: finalPayload?.calendarPayload || null,
            callPayload:     finalPayload?.callPayload     || null,
            scheduleProposalPayload: finalPayload?.scheduleProposalPayload || null,
            designPayload:   finalPayload?.designPayload   || null,
            schedulingPayload: finalPayload?.schedulingPayload || null,
            schedulingProposal: finalPayload?.schedulingProposal || null,
            // Contact + dated-message citations the server already
            // re-validated against the real record (issue #254 finding 3) —
            // rendered as clickable chips under the answer; see
            // AnswerSources below. `sourcesUnavailable` is a transient flag
            // from a genuine existence-check failure, distinct from "no
            // citations for this turn".
            sourcesPayload:     finalPayload?.sourcesPayload     || null,
            sourcesUnavailable: !!finalPayload?.sourcesUnavailable,
            // Stamped by the server when THIS answer drew on personal
            // (iCloud) mail — round-tripped in conversationHistory so a later
            // voice-mode turn redacts it instead of reading it again.
            personalMail: !!finalPayload?.personalMail,
            resolvedContacts: finalPayload?.resolvedContacts || [],
          }
          if (assistantIdx === -1) return [...m, finalized]
          const next = [...m]
          if (next[assistantIdx]) next[assistantIdx] = finalized
          return next
        })
      }

      if (finalContent && !finalPayload?.engineeringReceiptSaved) saveToDb('assistant', finalContent, { actionType: navTarget ? 'open_context' : finalPayload?.type, pageContext: page, payload: pickCardPayload(navTarget ? { ...finalPayload, contextPayload: navTarget } : finalPayload) })

      // "Take me to…" — go there now; the card stays behind as the way back in.
      if (navTarget) openJanetDestination(navTarget, { navigate, pushObject, visitObject })

      // The Edge chat endpoint queues draft_email rows without a body (a
      // Sonnet draft call is too slow for its budget) — kick off generation
      // now so the row is ready by the time the user opens the queue.
      // Fire-and-forget: if this dies, the queue detail's Regenerate covers it.
      if (finalPayload?.queuedDraft?.queueId) {
        regenerateDraft(finalPayload.queuedDraft.queueId).catch(() => {})
      }

      // Don't fire Cartesia TTS when Live Voice (RT) is active — RT handles audio
      if (voiceEnabled && !liveVoiceActive && finalContent) {
        // Safety net: reveal text after 2s even if OpenAI TTS never fires onAudioStart
        const fallback = setTimeout(revealMessage, 2000)
        speakFull(finalContent, () => { clearTimeout(fallback); revealMessage() })
      } else {
        revealMessage()
      }

      // Fire-and-forget — audit log must never block UI or delay setSending(false)
      logJanetDecision({
        principalId:    user?.id,
        principalName,
        mode:           'internal',
        authorityTier:  'T1',
        cpTier:         null,
        modelVersion:   finalPayload?.meta?.modelUsed || 'claude-sonnet-5',
        inputContext:   { page: page?.entityType ? { type: page.entityType, name: page.entityName, id: page.entityId } : null, userMessage: trimmed.slice(0, 500) },
        proposedAction: { type: finalType, content: finalContent.slice(0, 300) },
        scannerResults: {},
        finalAction:    finalType === 'answer' ? 'suppress' : 'draft',
        companyId:      'chatrone_us',
      }).catch(() => {})
    } catch (e) {
      const isTimeout = e.name === 'AbortError' || abortController.signal.aborted
      setMessages(m => {
        const errMsg = {
          role: 'assistant',
          content: isTimeout
            ? 'Taking too long — please try again.'
            : `Sorry — ${e.message || 'something went wrong'}.`,
          streaming: false,
          actionType: 'answer',
        }
        if (assistantIdx === -1) return [...m, errMsg]
        const next = [...m]
        if (next[assistantIdx]) next[assistantIdx] = errMsg
        return next
      })
    } finally {
      clearTimeout(abortTimer)
      setSending(false)
    }
  }, [sessionId, setMessages, messages, sending, page, user, principalName, voiceEnabled, speak, speakFull, stopVoice, stopListening, saveToDb, navigate, pushObject, visitObject, toast])

  // Stop turn-listener when panel closes or voice disabled
  useEffect(() => {
    if (!open || !voiceEnabled) stopListening()
  }, [open, voiceEnabled, stopListening])

  // Refs for transient state — lets the global Caps Lock handler read current
  // values without being in the effect dependency array (avoids constant restart)
  const isListeningRef = useRef(isListening)
  const sendingRef     = useRef(sending)
  const isSpeakingRef  = useRef(isSpeaking)
  const openRef        = useRef(open)
  useEffect(() => { isListeningRef.current = isListening }, [isListening])
  useEffect(() => { sendingRef.current = sending }, [sending])
  useEffect(() => { isSpeakingRef.current = isSpeaking }, [isSpeaking])
  useEffect(() => { openRef.current = open }, [open])

  // Right Option (AltRight) = keyboard hold-to-talk. Press → mic opens, release → send.
  // e.code === 'AltRight' is reliable on Mac/PC and never intercepted by macOS.
  // fn key is intercepted at the OS level (emoji/dictation setting) and may not
  // reach the browser. AltRight is never used as a web-app shortcut.
  // Active app-wide whenever voice is enabled.
  useEffect(() => {
    if (!sttSupported) { stopWakeWord(); return }

    const isPTT = (e) => e.code === 'AltRight'

    const onKeyDown = (e) => {
      if (!isPTT(e)) return
      e.preventDefault()
      if (e.repeat) return
      if (isListeningRef.current) return
      if (sendingRef.current) return
      setTimeout(() => {
        if (isListeningRef.current) return
        // Stop TTS before recording
        stopVoice()
        startListening(
          (finalText) => { if (finalText) sendMessage(finalText) },
          (errMsg)    => toast.error(errMsg),
        )
      }, 80)
    }

    const onKeyUp = (e) => {
      if (!isPTT(e)) return
      if (isListeningRef.current) stopListening()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup',   onKeyUp)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('keyup',   onKeyUp)
    }
  }, [sttSupported]) // eslint-disable-line react-hooks/exhaustive-deps

  // A question handed to the panel from outside — the demo rail's suggested
  // question (src/components/demo/DemoRail.jsx). Two paths because the panel
  // may or may not be mounted when the rail acts: a live `janet:ask` event
  // when it is, and a parked value in sessionStorage consumed on mount when it
  // is not. Both send exactly as if the visitor had typed it.
  useEffect(() => {
    const key = 'chatrone.janet.pendingAsk'
    const take = () => { try { const q = sessionStorage.getItem(key); if (q) sessionStorage.removeItem(key); return q } catch { return null } }
    const parked = take()
    if (parked) sendMessage(parked)
    const onAsk = (e) => { const q = e?.detail?.question; if (q) { take(); sendMessage(q) } }
    window.addEventListener('janet:ask', onAsk)
    return () => window.removeEventListener('janet:ask', onAsk)
  }, [sendMessage])

  // ── Mic button handler ────────────────────────────────────────────────────
  const handleMicStart = useCallback(() => {
    if (isListening) return
    // Stop TTS so it doesn't interfere with recording
    stopVoice()
    startListening(
      (finalText) => { if (finalText) sendMessage(finalText) },
      (errMsg)    => toast.error(errMsg),
    )
  }, [isListening, startListening, stopListening, stopVoice, sendMessage, toast])

  const handleMicStop = useCallback(() => {
    if (isListening) stopListening()
  }, [isListening, stopListening])

  // ── Action handlers ────────────────────────────────────────────────────────
  const handleGenerateDraft = (payload) => {
    setDraftPayload({
      contact: { id: payload.contactId, name: payload.contactName, email: payload.contactEmail || '' },
      draftType: payload.draftType || 'scheduling',
      voice: payload.voice || 'principal',
      context: payload.context || '',
      additionalRecipients: payload.additionalRecipients || [],
    })
    setOpen(false)
  }

  const handleCreateTask = async (idx, payload, calendarCb, { personal = false } = {}) => {
    // Defense in depth: `personalLocked` (api/_lib/personal-mail.js
    // personalizeTaskPayload) means this turn actually read personal-source
    // material, which must never reach the shared `tasks` table — enforced
    // here too, not only by CreateTaskCard's disabled checkbox, in case a
    // stale message object or a future caller ever passes `personal: false`
    // for a locked payload.
    const isPersonal = personal || payload?.personalLocked === true
    setTaskCreating(t => ({ ...t, [idx]: true }))
    try {
      if (isPersonal) {
        // The private personal list (personal_tasks) — never the team task list.
        const res = await addPersonalTask({ title: payload.title, notes: payload.notes, dueDate: payload.dueDate, ownerId: user?.id, createdBy: user?.id, source: 'janet' })
        if (!res.ok) throw new Error(res.error)
        toast.success('Added to your personal list')
        setMessages(m => m.map((msg, i) => i === idx ? { ...msg, taskCreated: true } : msg))
        return
      }
      const linked_contacts = (page?.entityType || '').toLowerCase() === 'contact' && page?.entityId
        ? [{ id: page.entityId, name: page.entityName }]
        : []
      const linked_deals = (page?.entityType || '').toLowerCase() === 'deal' && page?.entityId
        ? [{ id: page.entityId, name: page.entityName }]
        : []
      const linked_projects = (page?.entityType || '').toLowerCase() === 'project' && page?.entityId
        ? [{ id: page.entityId, name: page.entityName }]
        : []

      await createTask({
        title:       payload.title,
        description: payload.notes || null,
        due_date:    payload.dueDate || null,
        quadrant:  payload.quadrant || 2,
        status:    'pending',
        assigned_to: user?.id ? [user.id] : null,
        linked_contacts,
        linked_deals,
        linked_projects,
      })
      toast.success('Task created')
      setMessages(m => m.map((msg, i) => i === idx ? { ...msg, taskCreated: true } : msg))
      // If a calendar callback was passed (from CreateTaskCard's fallback button), fire it
      if (typeof calendarCb === 'function') calendarCb(user?.id)
    } catch (e) {
      toast.error(e.message || 'Could not create task')
    } finally {
      setTaskCreating(t => ({ ...t, [idx]: false }))
    }
  }

  const handleSuggestedPrompt = (text) => sendMessage(text)

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  const handleNewChat = () => {
    stopListening()
    onNewTopic?.(page)
    setMessages([])
    setInput('')
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  // Opened by the app-wide right-Option hold while the FAB was closed: start listening now.
  const pttStartedRef = useRef(false)
  useEffect(() => {
    if (!startListeningOnMount || pttStartedRef.current) return
    pttStartedRef.current = true
    const t = setTimeout(() => { try { handleMicStart() } catch {} }, 150)
    return () => clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startListeningOnMount])

  // Answers to a conversation Janet started (OpenerCard). The offer/noticing is
  // marked taken or let go on its own table (so Home and the emails agree),
  // then the topic carries on as a normal conversation.
  // Both fail closed: the work starts (or the item is let go) only after the
  // ruling is recorded, and the card stays answerable when it isn't. One
  // answer at a time — the buttons are held while a ruling is in flight.
  const [openerBusy, setOpenerBusy] = useState(null)
  const acceptOpener = useCallback(async (op, text) => {
    if (openerBusy) return
    setOpenerBusy(op.id)
    try {
      if (op.kind === 'offer') {
        // Check BEFORE any write whether this is a durable-draft offer — the
        // atomic path below (review round 5, finding #1) must decide which
        // route to take before the status flip happens, not after, or the
        // flip and the instruction would once again be two separate writes.
        //
        // Round 7, finding A1: a FAILED fetch here must never fall through
        // to the legacy accept path below (`ruleFor`/`ruleOnOffer`) — that
        // path does its own, completely independent fetchOffer call inside
        // ruleOnOffer, and a transient failure on THIS call (network blip,
        // anything) followed by that SECOND call succeeding would let a
        // durable-draft offer accept via the weak, non-atomic two-step path
        // with no instruction/anchor attached at all — exactly what round 6
        // retired the pre-migration fallback to prevent, reopened through a
        // different door. Whenever the offer's type cannot be established
        // at all (an error, or a legitimate not-found/not-visible result —
        // either way `current` is null), this fails closed: nothing is
        // accepted, the card stays answerable, and the legacy path below is
        // never reached for an 'offer' kind. A legitimate not-found offer
        // simply surfaces its own honest error here instead of a second,
        // redundant lookup discovering the same thing one line later.
        const { offer: current, error: fetchErr } = await fetchOffer(op.id)
        if (!current) {
          toast.error(fetchErr || "Couldn't record that — try again.")
          return
        }
        if (isDurableDraftOffer(current)) {
          const instruction = text || op.acceptText || 'Yes, go ahead.'
          // accept_janet_offer_with_instruction (supabase/migrations/
          // 20261018_janet_reply_alerts.sql) sets status='accepted' AND
          // records this exact instruction in the SAME UPDATE statement —
          // either both land or neither does, so there is no window where
          // the offer is claimable (status='accepted') with no durable
          // instruction attached. The worker reads offer.accepted_instruction
          // directly (worker/src/jobs/janet-offer-drafts.js), never only
          // offer.prompt, so a modified instruction actually reaches the
          // generated request.
          const atomic = await acceptOfferWithInstruction(op.id, instruction)
          if (atomic.ok) {
            setMessages(m => [...m, { role: 'user', content: instruction }])
            // draft_next_action is the DB trigger's (guard_janet_offer_draft_work,
            // 20261014133231) own honest, current-state wording — "pending_activation"
            // on a fresh accept reads "Waiting for the drafting service to
            // activate this work..." — never a hardcoded "I'm drafting a
            // reply now" that would be false whenever the drafting worker
            // isn't active yet.
            const ack = atomic.offer.draft_next_action || 'Okay — recorded. You can check progress any time.'
            setMessages(m => [...m, { role: 'assistant', content: ack }])
            // The instruction's AUTHORITATIVE record already landed atomically
            // above — this echo into janet_chat_messages is now purely
            // cosmetic continuity for a resumed topic's displayed history, so
            // a failure here warns rather than holds (the real work has
            // already started and the worker already has what it needs).
            const savedInstruction = await saveToDb('user', instruction, { pageContext: page })
            const savedAck = savedInstruction?.ok ? await saveToDb('assistant', ack, { pageContext: page }) : savedInstruction
            if (!savedInstruction?.ok || !savedAck?.ok) toast.warning('Started — the confirmation line may not reload, but nothing was lost.')
            navigate(`/janet/chat?offer=${encodeURIComponent(op.id)}`)
            return
          }
          // Round 6, finding A: "Missing atomic capability must leave new
          // work unclaimable with a clear error." No fallback: a durable-
          // draft offer's acceptance and its real instruction must land in
          // the SAME write (accept_janet_offer_with_instruction) or not at
          // all — falling through to the old two-step accept (status flip,
          // then a SEPARATE chat-message save) is exactly the "unsafe
          // missing-RPC fallback" this round retires, because it could flip
          // status to 'accepted' (claimable by the worker) with no durable
          // record of what was actually asked for, silently reducing to the
          // offer's own canned prompt. Whether the RPC is genuinely
          // unavailable (pre-migration) or failed for some other reason,
          // the offer stays open/unclaimed and the card stays answerable —
          // never a status flip this branch cannot also attach the real
          // instruction to.
          toast.error(atomic.unavailable
            ? "This can't be started yet — drafting isn't fully set up. Check back once it's enabled."
            : `Couldn't record that — try again. (${atomic.error})`)
          return
        }
      }
      // Genuinely legacy behavior, preserved separately: a noticing, or an
      // offer that is not durable-draft work (no instruction to persist —
      // accepting one is just an acknowledgment in the normal chat flow,
      // exactly as it worked before this feature existed). This path never
      // handles a durable-draft offer — that case already returned above,
      // one way or the other.
      const rule = ruleFor(op)
      if (rule) {
        const res = await rule(op.id, 'accepted', 'chat')
        if (!res.ok) { toast.error(res.unavailable ? res.error : `Couldn't record that — try again. (${res.error})`); return }
      }
      await sendMessage(text || op.acceptText || 'Yes, go ahead.', { acknowledgmentRecorded: true })
    } finally {
      setOpenerBusy(null)
    }
  }, [openerBusy, sendMessage, toast, navigate, saveToDb, page])
  const dismissOpener = useCallback(async (op, response = 'dismissed') => {
    if (openerBusy) return
    setOpenerBusy(op.id)
    try {
      const rule = ruleFor(op)
      if (rule) {
        const res = await rule(op.id, response, 'chat')
        if (!res.ok) { toast.error(res.unavailable ? res.error : `Couldn't record that — try again. (${res.error})`); return }
      }
      const reply = response === 'acknowledged' ? 'Got it — I’ll leave it with you.' : op.kind === 'noticing' ? "Got it — I won't raise that again." : op.kind === 'meeting' ? 'Okay. Good luck in the meeting.' : "Okay, I'll leave that one with you."
      setMessages(m => [...m, { role: 'assistant', content: reply }])
      saveToDb('assistant', reply, { pageContext: page })
    } finally {
      setOpenerBusy(null)
    }
  }, [openerBusy, toast, saveToDb, page])

  // An accepted offer (JanetChatPage ?offer=): send its instruction once, into
  // the fresh topic minted for it. Guarded at module level by the offer id so a
  // StrictMode replay or a re-render can never send it twice.
  useEffect(() => {
    if (!autoAsk?.key || !autoAsk.text) return
    if (autoAsk.sessionId && autoAsk.sessionId !== sessionId) return
    if (sending || dbLoading || AUTO_ASKED.has(autoAsk.key)) return
    AUTO_ASKED.add(autoAsk.key)
    sendMessage(autoAsk.text)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAsk, sessionId, sending, dbLoading])

  // Opened by a host's own explicit "Talk to Janet" doorway (e.g. the compact
  // Home dock below `lg` — issue #178) — the tap that mounted this panel with
  // the prop set IS the user gesture, so starting live voice here is not an
  // auto-start: it reuses handleStartLiveVoice, the exact same path the
  // header's "Live voice" button calls, never a second implementation. Fires
  // once per mount only; collapsing the host's compact dock unmounts this
  // panel, so re-expanding via the ordinary text doorway never re-triggers it.
  //
  // The "done" flag is set INSIDE the timeout callback, not when the effect
  // schedules it (2026-09-30 repair, Product Review finding on issue #178:
  // StrictMode broke this the same way it broke mountedRef above). React 18
  // StrictMode (src/main.jsx) replays every effect once in development —
  // mount → cleanup → mount again — synchronously, well before this timer's
  // real 150ms elapses. The old code set the ref true at schedule time, so
  // the FIRST mount already claimed it; that mount's cleanup then cancelled
  // its own timer (clearTimeout), and the replay's second mount saw the ref
  // already true and scheduled nothing — the tap silently never started
  // voice. Marking it inside the callback means a cancelled timer (real close
  // or StrictMode's replay cleanup) never claims the ref, so the next mount
  // is free to schedule a fresh timer that genuinely fires once. The
  // in-callback check is defense-in-depth against two timers both surviving
  // to fire (not expected in practice, since StrictMode's cleanup runs
  // synchronously before any real time passes).
  const liveVoiceStartedRef = useRef(false)
  useEffect(() => {
    if (!startLiveVoiceOnMount || liveVoiceStartedRef.current) return
    const t = setTimeout(() => {
      if (liveVoiceStartedRef.current) return
      liveVoiceStartedRef.current = true
      try { handleStartLiveVoice(voicePreflight) } catch {}
    }, 150)
    return () => clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLiveVoiceOnMount])

  const [topicsOpen, setTopicsOpen] = useState(false)
  const handleOpenWindow = () => {
    if (onOpenWindow) return onOpenWindow(sessionId)
    openJanetWindow(sessionId)
    if (layout === 'fab') onClose?.()
  }
  const isWindow = layout === 'window'

  const rows = Math.min(3, Math.max(1, (input.match(/\n/g) || []).length + 1))

  // Voice status label shown in header when active
  const voiceStatusLabel = isSpeaking ? 'Speaking…' : isListening ? 'Listening…' : null

  const topicTitle = activeSession?.title || (messages.find(m => m.role === 'user')?.content?.slice(0, 60)) || null

  return (
    <>
      <div ref={panelRef} data-janet-busy={(sending || isListening || isSpeaking || liveVoiceActive || !!input.trim()) ? '1' : undefined} className="relative flex flex-col h-full w-full min-h-0 bg-[color:var(--ns-raise)] overflow-hidden">
          <div className="flex flex-col h-full min-h-0">
            {/* Header — Janet's one identity, restrained: light purple tint + ✦ mark (design system §9), so it sits beside the navy shell instead of competing with it */}
            <div className="relative shrink-0 px-3.5 py-2.5 bg-janet-50 text-janet-900 border-b border-janet-100" style={{ paddingTop: isWindow ? undefined : 'max(0.625rem, env(safe-area-inset-top))' }}>
              {topicsOpen && (
                <TopicsDropdown
                  sessions={sessions}
                  activeId={sessionId}
                  onPick={(id) => { setTopicsOpen(false); onPickTopic?.(id) }}
                  onNew={() => { setTopicsOpen(false); handleNewChat() }}
                  onOpenAll={() => { setTopicsOpen(false); navigate('/janet/chat'); if (layout === 'fab') onClose?.() }}
                  onClose={() => setTopicsOpen(false)}
                />
              )}
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5">
                {onToggleSidebar && (
                  <button
                    onClick={onToggleSidebar}
                    aria-expanded={sidebarOpen}
                    className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold transition-all shrink-0 ${sidebarOpen ? 'bg-janet-200 text-janet-900' : 'bg-[color:var(--ns-raise)] border border-janet-200 text-janet-700 hover:bg-janet-100'}`}
                    title={sidebarOpen ? 'Hide topics' : 'Show topics — resume or start a conversation'}
                  >
                    {sidebarOpen ? <PanelLeftClose size={13} /> : <PanelLeftOpen size={13} />}
                    <span>Topics</span>
                  </button>
                )}
                <div className="w-7 h-7 rounded-full bg-janet-600 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-white">✦</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold leading-tight truncate">{isWindow && topicTitle ? topicTitle : 'Janet'}</p>
                  <p className="text-[10px] text-janet-600 leading-tight truncate">
                    {voiceStatusLabel
                      ? <span className="text-teal-700 animate-pulse">{voiceStatusLabel}</span>
                      : (!isWindow && topicTitle) ? topicTitle : 'T1 — you review before send'}
                  </p>
                </div>
                {showTopics && sessionsAvailable && (
                  <div>
                    <button
                      onClick={() => setTopicsOpen(v => !v)}
                      className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${topicsOpen ? 'bg-janet-200 text-janet-900' : 'bg-[color:var(--ns-raise)] border border-janet-200 text-janet-700 hover:bg-janet-100'}`}
                      title="Topics — resume an earlier conversation"
                    >
                      <MessageSquare size={11} />
                      <span>Topics</span>
                      <ChevronDown size={10} />
                    </button>
                  </div>
                )}
                {/* Below `sm` (640px CSS px) this header is narrow enough that
                    every control plus Close no longer fits the row — "open in
                    her own window" is the least essential control at that
                    width (there's no separate-window concept on a narrow
                    phone anyway) and the first to collapse, matching the
                    same sm: breakpoint already used below to hide New's
                    label. This keeps Close fully visible and tappable
                    (2026-09-30 visual-evidence finding). */}
                {!isWindow && (
                  <button
                    onClick={handleOpenWindow}
                    className="hidden sm:inline-flex shrink-0 text-janet-500 hover:text-janet-800 transition-colors"
                    title="Open Janet in her own window"
                  >
                    <Maximize2 size={12} />
                  </button>
                )}
                {isSpeaking && (
                  <button
                    onClick={stopVoice}
                    className="text-[10px] text-amber-600 hover:text-amber-800 flex items-center gap-0.5 transition-colors animate-pulse"
                    title="Stop speaking"
                  >
                    <Square size={10} />
                  </button>
                )}
                <button
                  onClick={liveVoiceActive ? handleStopLiveVoice : () => handleStartLiveVoice()}
                  className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                    liveVoiceActive
                      ? 'bg-teal-100 text-teal-800 hover:bg-red-100 hover:text-red-700'
                      : 'bg-[color:var(--ns-raise)] border border-janet-200 text-janet-700 hover:bg-teal-50 hover:text-teal-800'
                  }`}
                  title={liveVoiceActive ? 'End live voice session' : 'Start live voice conversation with Janet'}
                >
                  <Radio size={11} className={liveVoiceActive ? 'animate-pulse' : ''} />
                  <span className={isWindow ? 'hidden sm:inline' : 'hidden'}>{liveVoiceActive ? 'Live' : 'Live voice'}</span>
                </button>
                <button
                  onClick={toggleVoice}
                  className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                    voiceEnabled
                      ? 'bg-teal-100 text-teal-800 hover:bg-teal-200'
                      : 'bg-[color:var(--ns-raise)] border border-janet-200 text-janet-700 hover:bg-janet-100'
                  }`}
                  title={voiceEnabled ? 'Voice on — click to mute' : 'Voice off — click to enable'}
                >
                  {voiceEnabled ? <Volume2 size={13} /> : <VolumeX size={13} />}
                  <span className={isWindow ? 'hidden sm:inline' : 'hidden'}>TTS</span>
                </button>
                {/* Same narrow-width collapse as Maximize2 above — New is a
                    duplicate of the Topics dropdown's own "New" action, so
                    it's the second control to give way below `sm`, never
                    Close. */}
                {messages.length > 0 && (
                  <button
                    onClick={handleNewChat}
                    className="hidden sm:flex shrink-0 text-[10px] text-janet-600 hover:text-janet-900 items-center gap-0.5 transition-colors"
                    title="New topic"
                  >
                    <RefreshCw size={10} />
                    <span className="hidden sm:inline">New</span>
                  </button>
                )}
                {onClose && !isWindow && (
                  <button
                    onClick={() => onClose()}
                    className="shrink-0 w-6 h-6 rounded-full bg-janet-100 flex items-center justify-center text-janet-700 hover:bg-janet-200 hover:text-janet-900"
                    title={layout === 'docked' ? 'Let Janet recede' : 'Close'}
                    aria-label={layout === 'docked' ? 'Let Janet recede' : 'Close Janet'}
                  >
                    {layout === 'docked' ? <ChevronsRight size={13} /> : <X size={13} />}
                  </button>
                )}
              </div>
              {(page?.entityName || activeSession?.entity_name) && (
                <div className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded-md bg-[color:var(--ns-raise)] border border-janet-200">
                  <Layers size={10} className="text-janet-500 shrink-0" />
                  <span className="text-[10px] text-janet-500 uppercase tracking-wider shrink-0">{page?.entityType || activeSession?.entity_type}</span>
                  <span className="text-[11px] text-janet-900 font-medium truncate">{page?.entityName || activeSession?.entity_name}</span>
                </div>
              )}
            </div>

            {/* Live Voice Overlay */}
            {liveVoiceActive && (
              <div className="absolute inset-0 top-[64px] bg-janet-900/97 flex flex-col items-center justify-center z-10">
                {/* Status orb */}
                <div className={`relative w-24 h-24 rounded-full flex items-center justify-center mb-6 transition-all duration-300 ${
                  rt.status === 'connecting'  ? 'bg-white/10' :
                  rt.status === 'listening'   ? 'bg-white/10 ring-4 ring-teal-400/40' :
                  rt.status === 'thinking'    ? 'bg-white/10 ring-4 ring-amber-400/40' :
                  rt.status === 'speaking'    ? 'bg-teal-600/30 ring-4 ring-teal-400/60' :
                  rt.status === 'error'       ? 'bg-red-900/40 ring-4 ring-red-400/40' :
                  'bg-white/10'
                }`}>
                  {rt.status === 'connecting' && <Loader2 size={32} className="text-white animate-spin" />}
                  {rt.status === 'listening'  && (
                    <div className="flex gap-1 items-end h-8">
                      {[0,1,2,3,4].map(i => (
                        <div key={i} className="w-1.5 bg-teal-400 rounded-full animate-bounce"
                          style={{ height: `${12 + (i % 3) * 8}px`, animationDelay: `${i * 80}ms` }} />
                      ))}
                    </div>
                  )}
                  {rt.status === 'thinking'   && <Loader2 size={32} className="text-amber-400 animate-spin" />}
                  {rt.status === 'speaking'   && (
                    <div className="flex gap-1 items-end h-8">
                      {[0,1,2,3,4].map(i => (
                        <div key={i} className="w-1.5 bg-teal-300 rounded-full animate-bounce"
                          style={{ height: `${8 + (i % 5) * 6}px`, animationDelay: `${i * 60}ms` }} />
                      ))}
                    </div>
                  )}
                  {rt.status === 'error'      && <span className="text-3xl">⚠</span>}
                </div>

                {/* Status label */}
                <p className="text-sm font-medium text-white/90 mb-1">
                  {rt.status === 'connecting' ? 'Connecting…' :
                   rt.status === 'listening'  ? 'Listening' :
                   rt.status === 'thinking'   ? 'Thinking…' :
                   rt.status === 'speaking'   ? 'Speaking' :
                   rt.status === 'error'      ? 'Error' : ''}
                </p>

                {/* Live transcripts */}
                <div className="w-full px-6 text-center space-y-2 min-h-[60px]">
                  {rt.transcript.user && (
                    <p className="text-[11px] text-white/60 italic">"{rt.transcript.user}"</p>
                  )}
                  {rt.transcript.janet && (
                    <p className="text-[12px] text-white/80 leading-snug">{rt.transcript.janet}</p>
                  )}
                  {rt.error && (
                    <p className="text-[11px] text-red-400">{rt.error}</p>
                  )}
                </div>

                {/* End session button */}
                <button
                  onClick={handleStopLiveVoice}
                  className="mt-8 flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
                >
                  <Square size={13} />
                  End session
                </button>
                <p className="mt-3 text-[10px] text-white/50">Just speak — Janet is listening</p>
              </div>
            )}

            {/* Messages */}
            <div
              ref={scrollRef}
              className={`min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden py-3 space-y-3 bg-[color:var(--ns-paper)] ${isWindow ? 'px-4 sm:px-6' : 'px-3'}`}
            >
             <div className={isWindow ? 'max-w-3xl mx-auto space-y-3' : 'space-y-3'}>
              {messages.length === 0 && (
                <div className="py-2">
                  <JanetBubble>
                    Hi — I'm Janet. I can brief you on what's on this page, help you draft an email, or turn a thought into a task. What do you need?
                  </JanetBubble>
                  <div className="mt-3 space-y-1.5 pl-8">
                    <p className="text-[10px] text-[color:var(--ns-faint)] uppercase tracking-wider font-semibold px-1">Try asking</p>
                    {prompts.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => handleSuggestedPrompt(p.text)}
                        className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-[color:var(--ns-ink)] bg-[color:var(--ns-raise)] border border-[color:var(--ns-hair)] hover:border-[color:var(--ns-hair)] hover:bg-[color:var(--ns-hair-soft)] hover:text-[color:var(--ns-ink)] transition-colors"
                      >
                        <p.icon size={11} className="text-[color:var(--ns-faint)] shrink-0" />
                        <span className="truncate">{p.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => {
                if (msg.role === 'user') return <UserBubble key={i} text={msg.content} />
                return (
                  <div key={i} className="space-y-0">
                    <JanetBubble>
                      {/* While streaming, Janet's output is raw tool-use JSON being assembled
                          character by character — never display it. Show dots until done. */}
                      {msg.streaming || msg.thinking ? (
                        <div className="flex gap-1 py-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--ns-ink)] animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--ns-ink)] animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--ns-ink)] animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      ) : (
                        <FormattedAnswer text={msg.content} />
                      )}
                      {!msg.streaming && msg.headsUp && safeHeadsUpLinks(msg.headsUp.links).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {safeHeadsUpLinks(msg.headsUp.links).map(l => (
                            <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer"
                              className="text-[11px] px-2 py-0.5 rounded-full border border-[color:var(--ns-hair)] text-[color:var(--ns-ink-2)] hover:text-[color:var(--ns-ink)]">
                              {l.label}
                            </a>
                          ))}
                        </div>
                      )}
                      {!msg.streaming && !msg.thinking && (msg.sourcesPayload?.length > 0 || msg.sourcesUnavailable) && (
                        <AnswerSources sources={msg.sourcesPayload} unavailable={msg.sourcesUnavailable} />
                      )}
                      {msg.actionType === 'draft_email' && msg.draftPayload && (
                        <DraftEmailCard
                          payload={msg.draftPayload}
                          queuedDraft={msg.queuedDraft}
                          onGenerate={() => handleGenerateDraft(msg.draftPayload)}
                        />
                      )}
                      {msg.actionType === 'create_task' && msg.taskPayload && !msg.taskCreated && (
                        <CreateTaskCard
                          payload={msg.taskPayload}
                          creating={!!taskCreating[i]}
                          onCreate={(calCb, opts) => handleCreateTask(i, msg.taskPayload, calCb, opts)}
                        />
                      )}
                      {msg.taskCreated && (
                        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-green-700">
                          <CheckCircle2 size={11} /> Task added to your list
                        </div>
                      )}
                      {/* Show calendar card for both propose_calendar_events AND create_task with calendarPayload */}
                      {msg.calendarPayload && (
                        <CalendarEventsCard
                          payload={msg.calendarPayload}
                          userId={user?.id}
                          outcome={messages.find(m => m.calendarResult?.proposalId === (msg.calendarPayload.proposalId || JSON.stringify(msg.calendarPayload)))?.calendarResult}
                          onAdded={result => {
                            const calendarResult = { ...result, proposalId: msg.calendarPayload.proposalId || JSON.stringify(msg.calendarPayload) }
                            const content = result.status === 'created'
                              ? `Calendar confirmed ${result.created.length} event(s).`
                              : 'The calendar write was not fully confirmed. Check the calendar before retrying.'
                            const receipt = { role: 'assistant', content, calendarResult, personalMail: !!msg.personalMail }
                            setMessages(current => [...current, receipt])
                            saveToDb('assistant', content, { payload: pickCardPayload(receipt) })
                          }}
                        />
                      )}
                      {msg.actionType === 'propose_schedule_items' && msg.scheduleProposalPayload && (
                        <ScheduleProposalCard payload={msg.scheduleProposalPayload} />
                      )}
                      {msg.actionType === 'request_scheduling_approval' && msg.schedulingPayload && (
                        <SchedulingApprovalCard payload={msg.schedulingPayload} user={user} />
                      )}
                      {msg.actionType === 'propose_outbound_call' && msg.callPayload && (
                        <OutboundCallCard payload={msg.callPayload} onRCDial={rc?.clickToDial} user={user} />
                      )}
                      {msg.actionType === 'propose_design_work' && msg.designPayload && (
                        <DesignWorkCard payload={msg.designPayload} />
                      )}
                      {msg.actionType === 'open_context' && msg.contextPayload && (
                        <OpenContextCard payload={msg.contextPayload} />
                      )}
                      {msg.actionType === 'janet_opener' && msg.openerPayload && (
                        <OpenerCard
                          payload={msg.openerPayload}
                          live={i === messages.length - 1 && !sending}
                          busy={openerBusy === msg.openerPayload.id}
                          onAccept={(text) => acceptOpener(msg.openerPayload, text)}
                          onDismiss={(response) => dismissOpener(msg.openerPayload, response)}
                        />
                      )}
                    </JanetBubble>
                  </div>
                )
              })}

              {sending && !messages.some(m => m.streaming) && <TypingIndicator />}
             </div>
            </div>

            {/* Input */}
            <div className="shrink-0 border-t border-[color:var(--ns-hair)] bg-[color:var(--ns-raise)] p-2.5" style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}>
             <div className={isWindow ? 'max-w-3xl mx-auto' : ''}>
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={rows}
                  placeholder={isListening ? 'Listening…' : 'Ask Janet anything…'}
                  className={`flex-1 min-w-0 resize-none px-3 py-2 text-base md:text-sm rounded-lg border focus:outline-none bg-[color:var(--ns-raise)] text-[color:var(--ns-ink)] placeholder:text-[color:var(--ns-faint)] ${
                    isListening
                      ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-200'
                      : 'border-[color:var(--ns-hair)] focus:border-janet-400 focus:ring-1 focus:ring-janet-200'
                  }`}
                  style={{ minHeight: 36, maxHeight: 90 }}
                  readOnly={isListening}
                />
                {sttSupported && (
                  <button
                    onPointerDown={(e) => {
                      e.preventDefault()
                      if (sending) return
                      handleMicStart()
                    }}
                    onPointerUp={handleMicStop}
                    onPointerLeave={handleMicStop}
                    disabled={sending}
                    className={`shrink-0 w-11 h-11 md:w-9 md:h-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed select-none ${
                      isListening
                        ? 'bg-red-500 text-white animate-pulse hover:bg-red-600'
                        : 'bg-[color:var(--ns-hair-soft)] text-[color:var(--ns-ink-2)] hover:bg-[color:var(--ns-hair)]'
                    }`}
                    title={isListening ? 'Release to send' : 'Hold to talk (or hold right Option key)'}
                  >
                    {isListening ? <MicOff size={14} /> : <Mic size={14} />}
                  </button>
                )}
                <button
                  onClick={() => sendMessage(input)}
                  disabled={sending || !input.trim() || isListening}
                  className="shrink-0 w-11 h-11 md:w-9 md:h-9 rounded-lg bg-janet-700 text-white flex items-center justify-center hover:bg-janet-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Send"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
              <p className="text-[9px] text-[color:var(--ns-faint)] mt-1 px-0.5">
                {isListening
                  ? '🎙 Listening… release to send'
                  : sttSupported
                    ? <>Hold the mic to talk<span className="hidden sm:inline"> (or right Option key)</span></>
                    : 'Janet drafts — you review and send. Never acts autonomously.'}
              </p>
             </div>
            </div>
          </div>
      </div>

      {draftPayload && (
        <JanetDraftModal
          isOpen={!!draftPayload}
          onClose={() => setDraftPayload(null)}
          draftType={draftPayload.draftType}
          voice={draftPayload.voice || 'principal'}
          contact={draftPayload.contact}
          context={draftPayload.context}
          additionalRecipients={draftPayload.additionalRecipients || []}
          subject=""
          onSent={() => setDraftPayload(null)}
        />
      )}
    </>
  )
}
